import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Grid, Card, CardContent,
  CardActions, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Alert, IconButton, Divider,
  CircularProgress, Tooltip, Table, TableBody, TableCell, TableRow,
} from '@mui/material';
import {
  Add, Delete, Power, CheckCircle, Cancel, Refresh, Wifi, WifiOff, Edit, Save, Login,
} from '@mui/icons-material';
import axios from 'axios';

const BROKER_OPTIONS = [
  { value: 'shoonya', label: 'Shoonya (Finvasia)', fields: ['client_id', 'api_key', 'secret_key', 'password', 'totp_key'] },
  { value: 'zerodha', label: 'Zerodha (Kite)', fields: ['client_id', 'api_key', 'secret_key', 'password', 'totp_key'] },
  { value: 'angelone', label: 'AngelOne (SmartAPI)', fields: ['client_id', 'api_key', 'password', 'totp_key'] },
];

const FIELD_LABELS = {
  shoonya: { client_id: 'Vendor Code / User ID', api_key: 'API Key', secret_key: 'IMEI', password: 'Password', totp_key: 'TOTP Secret' },
  zerodha: { client_id: 'User ID', api_key: 'API Key', secret_key: 'API Secret', password: 'Password', totp_key: 'TOTP Secret' },
  angelone: { client_id: 'Client Code', api_key: 'API Key', password: 'MPIN / Password', totp_key: 'TOTP Secret' },
};

const IST_FMT = { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true };
const EMPTY_FORM = { broker_name: 'shoonya', client_id: '', api_key: '', secret_key: '', password: '', totp_key: '' };

export default function BrokerConfig({ apiUrl }) {
  const [configs, setConfigs] = useState([]);
  const [brokerStatus, setBrokerStatus] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [reconnecting, setReconnecting] = useState(null);
  const [addForm, setAddForm] = useState({ ...EMPTY_FORM });
  const [editForm, setEditForm] = useState({ ...EMPTY_FORM });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchConfigs(); checkBrokerStatus(); }, []);

  const fetchConfigs = async () => {
    try {
      const res = await axios.get(`${apiUrl}/broker/config`, { headers });
      setConfigs(res.data);
    } catch (err) { console.error(err); }
  };

  const checkBrokerStatus = async () => {
    try {
      const res = await axios.get(`${apiUrl}/broker/status`, { headers });
      setBrokerStatus(res.data);
    } catch (err) { console.error(err); }
  };

  const handleSave = async (andLogin = false) => {
    setLoading(true); setError(''); setSuccess('');
    try {
      const url = andLogin ? `${apiUrl}/broker/config/save-and-login` : `${apiUrl}/broker/config`;
      const res = await axios.post(url, addForm, { headers });
      setSuccess(andLogin && res.data?.is_logged_in ? 'Saved & logged in!' : 'Broker saved.');
      setOpenAdd(false);
      setAddForm({ ...EMPTY_FORM });
      fetchConfigs(); checkBrokerStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally { setLoading(false); }
  };

  const handleEditSave = async () => {
    if (!editId) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const payload = {};
      if (editForm.broker_name) payload.broker_name = editForm.broker_name;
      if (editForm.client_id) payload.client_id = editForm.client_id;
      if (editForm.api_key) payload.api_key = editForm.api_key;
      if (editForm.secret_key) payload.secret_key = editForm.secret_key;
      if (editForm.password) payload.password = editForm.password;
      if (editForm.totp_key) payload.totp_key = editForm.totp_key;

      await axios.put(`${apiUrl}/broker/config/${editId}`, payload, { headers });
      setSuccess('Configuration updated.');
      setOpenEdit(false); setEditId(null);
      fetchConfigs(); checkBrokerStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Update failed');
    } finally { setLoading(false); }
  };

  const openEditDialog = (config) => {
    setEditId(config.id);
    setEditForm({
      broker_name: config.broker_name,
      client_id: config.client_id,
      api_key: '', secret_key: '', password: '', totp_key: '',
    });
    setOpenEdit(true);
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Delete this broker configuration?')) return;
    try {
      await axios.delete(`${apiUrl}/broker/config/${configId}`, { headers });
      setSuccess('Deleted');
      fetchConfigs(); checkBrokerStatus();
    } catch (err) { setError('Failed to delete'); }
  };

  const handleReconnect = async (configId) => {
    setReconnecting(configId);
    try {
      const res = await axios.post(`${apiUrl}/broker/reconnect/${configId}`, {}, { headers });
      if (res.data.success) setSuccess('Reconnected!');
      else setError(res.data.error || 'Reconnect failed');
      fetchConfigs(); checkBrokerStatus();
    } catch (err) { setError('Reconnect failed'); }
    finally { setReconnecting(null); }
  };

  const selectedAddBroker = BROKER_OPTIONS.find(b => b.value === addForm.broker_name) || BROKER_OPTIONS[0];
  const addLabels = FIELD_LABELS[addForm.broker_name] || FIELD_LABELS.shoonya;
  const selectedEditBroker = BROKER_OPTIONS.find(b => b.value === editForm.broker_name) || BROKER_OPTIONS[0];
  const editLabels = FIELD_LABELS[editForm.broker_name] || FIELD_LABELS.shoonya;

  return (
    <Box data-testid="broker-config-page">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Broker Configuration</Typography>
          <Typography variant="caption" color="text.secondary">Auto-login on startup. Session auto-refresh every 15 min.</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" size="small" startIcon={<Refresh />}
            onClick={() => { fetchConfigs(); checkBrokerStatus(); }} data-testid="refresh-broker-btn">Refresh</Button>
          <Button variant="contained" size="small" startIcon={<Add />}
            onClick={() => setOpenAdd(true)} data-testid="add-broker-btn">Add Broker</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Connection Status */}
      <Paper sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: brokerStatus?.connected ? 'success.main' : 'divider' }} variant="outlined">
        <Box display="flex" alignItems="center" gap={2}>
          {brokerStatus?.connected ? <Wifi color="success" /> : <WifiOff color="disabled" />}
          <Typography variant="subtitle2" fontWeight={600}>
            {brokerStatus?.connected ? `Connected to ${brokerStatus.broker?.toUpperCase()}` : 'No active broker connection'}
          </Typography>
        </Box>
      </Paper>

      {/* Broker Cards */}
      <Grid container spacing={2}>
        {configs.map(config => {
          const isOnline = brokerStatus?.sessions?.some(s => s.config_id === config.id && s.connected);
          const labels = FIELD_LABELS[config.broker_name] || FIELD_LABELS.shoonya;
          return (
            <Grid item xs={12} md={6} key={config.id}>
              <Card variant="outlined"
                sx={{ border: '1px solid', borderColor: isOnline ? 'success.main' : config.is_active ? 'warning.main' : 'divider' }}
                data-testid={`broker-card-${config.id}`}>
                <CardContent sx={{ pb: 0 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" fontWeight={700}>{config.broker_name.toUpperCase()}</Typography>
                    <Box display="flex" gap={0.5}>
                      {config.is_active && <Chip label="Active" color="success" size="small" sx={{ height: 22, fontSize: '0.7rem' }} />}
                      {isOnline
                        ? <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Online" color="success" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                        : <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Offline" color="error" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                      }
                    </Box>
                  </Box>

                  {/* All Fields Table */}
                  <Table size="small" sx={{ '& td': { py: 0.3, border: 0 }, '& td:first-of-type': { color: 'text.secondary', width: 120, fontSize: '0.75rem', fontWeight: 600 } }}>
                    <TableBody>
                      <TableRow>
                        <TableCell>{labels.client_id || 'Client ID'}</TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600}>{config.client_id}</Typography></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{labels.api_key || 'API Key'}</TableCell>
                        <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{config.api_key_masked || '-'}</Typography></TableCell>
                      </TableRow>
                      {config.secret_key_masked && (
                        <TableRow>
                          <TableCell>{labels.secret_key || 'Secret Key'}</TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{config.secret_key_masked}</Typography></TableCell>
                        </TableRow>
                      )}
                      {config.password_masked && (
                        <TableRow>
                          <TableCell>{labels.password || 'Password'}</TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{config.password_masked}</Typography></TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell>{labels.totp_key || 'TOTP'}</TableCell>
                        <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{config.totp_key_masked || '-'}</Typography></TableCell>
                      </TableRow>
                      {config.last_login && (
                        <TableRow>
                          <TableCell>Last Login</TableCell>
                          <TableCell><Typography variant="caption">{new Date(config.last_login).toLocaleString('en-IN', IST_FMT)}</Typography></TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>

                  {config.login_error && (
                    <Alert severity="error" sx={{ mt: 1, py: 0 }} icon={false}>
                      <Typography variant="caption">{config.login_error}</Typography>
                    </Alert>
                  )}
                </CardContent>
                <Divider sx={{ mt: 1 }} />
                <CardActions sx={{ px: 2, py: 1, justifyContent: 'flex-start', gap: 0.5 }}>
                  <Button size="small" variant="outlined" startIcon={<Edit />}
                    onClick={() => openEditDialog(config)} data-testid={`broker-edit-${config.id}`}>
                    Edit
                  </Button>
                  <Button size="small" variant="outlined" color="warning"
                    startIcon={reconnecting === config.id ? <CircularProgress size={14} /> : <Refresh />}
                    onClick={() => handleReconnect(config.id)} disabled={reconnecting === config.id}
                    data-testid={`broker-reconnect-${config.id}`}>
                    Reconnect
                  </Button>
                  <Button size="small" variant="outlined" color="error" startIcon={<Delete />}
                    onClick={() => handleDelete(config.id)} data-testid={`broker-delete-${config.id}`}>
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
        {configs.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">No broker configured. Click "Add Broker" to connect your trading account.</Alert>
          </Grid>
        )}
      </Grid>

      {/* ADD Dialog */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Broker</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>Credentials are encrypted at rest.</Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Broker</InputLabel>
                <Select value={addForm.broker_name} label="Broker"
                  onChange={e => setAddForm({ ...addForm, broker_name: e.target.value })}
                  data-testid="broker-select">
                  {BROKER_OPTIONS.map(b => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {selectedAddBroker.fields.map(field => (
              <Grid item xs={12} key={field}>
                <TextField fullWidth size="small"
                  label={addLabels[field] || field}
                  type={['password', 'totp_key', 'secret_key'].includes(field) ? 'password' : 'text'}
                  value={addForm[field]}
                  onChange={e => setAddForm({ ...addForm, [field]: e.target.value })}
                  data-testid={`broker-${field}-input`}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button onClick={() => handleSave(false)} variant="outlined" startIcon={<Save />}
            disabled={loading || !addForm.client_id || !addForm.api_key || !addForm.totp_key}
            data-testid="broker-save-btn">
            {loading ? 'Saving...' : 'Save'}
          </Button>
          <Button onClick={() => handleSave(true)} variant="contained" startIcon={<Login />}
            disabled={loading || !addForm.client_id || !addForm.api_key || !addForm.totp_key}
            data-testid="broker-save-login-btn">
            {loading ? 'Saving...' : 'Save & Login'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT Dialog */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Broker Configuration</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>Leave secret fields blank to keep existing values.</Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth disabled>
                <InputLabel>Broker</InputLabel>
                <Select value={editForm.broker_name} label="Broker" data-testid="edit-broker-select">
                  {BROKER_OPTIONS.map(b => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {selectedEditBroker.fields.map(field => (
              <Grid item xs={12} key={field}>
                <TextField fullWidth size="small"
                  label={editLabels[field] || field}
                  type={['password', 'totp_key', 'secret_key'].includes(field) ? 'password' : 'text'}
                  value={editForm[field]}
                  onChange={e => setEditForm({ ...editForm, [field]: e.target.value })}
                  placeholder={field !== 'client_id' ? '(leave blank to keep existing)' : ''}
                  data-testid={`edit-${field}-input`}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained"
            disabled={loading || !editForm.client_id}
            data-testid="broker-update-btn">
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
